import { v4 as uuid } from 'uuid';

import {
  InputFieldConfig,
  type ActiveCohortBuilderItemLegacy,
  DerivedEventLegacy,
  SimpleEventLegacy,
  FilterLegacy,
  SubjectVariableTypeLegacy,
} from '@verantos-dev/types-cohorts';
import { ConceptGroup } from '@verantos-dev/types-concepts';

import { buildSubjectVariableConfigMap } from './buildConfigMap';
import { SUBJECT_VARIABLE_FILTER_MAPPING, SubjectVariableFilterKey } from './filterTypes';

const initializeFilter = (filter: FilterLegacy, filterType: SubjectVariableFilterKey): void => {
  if (filter.inputFields.length === 0) {
    if (filterType === 'countFilter') {
      filter.inputFields = [
        { id: uuid(), value: '' },
        { id: uuid(), value: '' },
        { id: uuid(), value: '' },
      ];
    } else {
      filter.inputFields = [{ id: uuid(), value: '' }];
    }
  }
  if (filterType !== 'countFilter') {
    // If we have 1 field with a value, create operator and value fields
    if (filter.inputFields.length === 1 && filter.inputFields[0].value) {
      filter.inputFields = [
        { id: filter.inputFields[0].id, value: filter.inputFields[0].value },
        { id: uuid(), value: filterType === 'conceptFilters' ? 'IN' : '' },
        { id: uuid(), value: '' },
      ];
    }
    // If we have 2 fields (attr + op) but no value field, create the value field
    // This handles the case where operator was set but value field wasn't created yet
    else if (filter.inputFields.length === 2 && filter.inputFields[0].value) {
      filter.inputFields = [
        { id: filter.inputFields[0].id, value: filter.inputFields[0].value },
        { id: filter.inputFields[1].id, value: filter.inputFields[1].value },
        { id: uuid(), value: '' },
      ];
    }
  }
};

export const addOrMutateSubjectVariableFilters = (
  subjectVariable: ActiveCohortBuilderItemLegacy
): void => {
  const { numericFilters, conceptFilters } = subjectVariable;

  // Initialize array-based filters
  const filterPairs = [
    [numericFilters, 'numericFilters'],
    [conceptFilters, 'conceptFilters'],
  ] as const;

  filterPairs.forEach(([filters, filterType]) => {
    filters?.forEach((filter) => {
      initializeFilter(filter, filterType);
    });
  });

  // Initialize count filter if COUNT is chosen
  if (subjectVariable.type === SubjectVariableTypeLegacy.COUNT && !subjectVariable.countFilter) {
    const newCountFilter: FilterLegacy = {
      id: uuid(),
      inputFields: [],
    };
    initializeFilter(newCountFilter, 'countFilter');
    subjectVariable.countFilter = newCountFilter;
  }
};

export const reduceSubjectVariableConfigMap = (
  acc: Record<string, InputFieldConfig>,
  key: 'numericFilters' | 'conceptFilters' | 'countFilter',
  {
    subjectVariable,
    simpleEvents,
    derivedEvents,
    conceptGroups,
  }: {
    subjectVariable: ActiveCohortBuilderItemLegacy;
    simpleEvents: SimpleEventLegacy[];
    derivedEvents: DerivedEventLegacy[];
    conceptGroups: ConceptGroup[];
  }
): Record<string, InputFieldConfig> => {
  // For DEMOGRAPHICS types (numeric, concept filters)
  if (key !== 'countFilter' && Array.isArray(subjectVariable[key])) {
    const filters = subjectVariable[key] as FilterLegacy[];
    filters?.forEach((filter: FilterLegacy) => {
      if (filter) {
        const configMap = buildSubjectVariableConfigMap(
          filter,
          subjectVariable.type as SubjectVariableTypeLegacy,
          SUBJECT_VARIABLE_FILTER_MAPPING[key],
          simpleEvents,
          derivedEvents,
          conceptGroups
        );
        Object.assign(acc, configMap);
      }
    });
  }
  // For COUNT type (count filter)
  else if (key === 'countFilter' && subjectVariable.countFilter) {
    const filter = subjectVariable.countFilter as FilterLegacy;
    if (filter) {
      const configMap = buildSubjectVariableConfigMap(
        filter,
        subjectVariable.type as SubjectVariableTypeLegacy,
        SUBJECT_VARIABLE_FILTER_MAPPING.countFilter,
        simpleEvents,
        derivedEvents,
        conceptGroups
      );
      Object.assign(acc, configMap);
    }
  }

  return acc;
};
